import React from "react";
import { format } from "date-fns";
import { DATE_FNS_LONG_DATE_FORMAT } from "shared/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faStickyNote } from "@fortawesome/free-solid-svg-icons";

import { WithIcon, WithTooltip } from "shared/table-helpers";
import { CONTEXT_STATUS } from "shared/constants";
import { inputFilter } from "components/task-list/lib/constants";
import { Select } from "components/form-inputs/inputs/select";
import { statusBooleanOptions } from "shared/constants/dropdown-options";
import { Checkbox } from "../../form-inputs/inputs/checkbox";
import { DueDateFilterButton } from "../../table/components/dueDateFilterButton";

export const getColumns = (selectedRows, selectAllRows, updateRowSelection, showNotes, filterable) => [
    {
        Header: (props) => {
            const keys = props.data.map((p) => p._original).filter(({ key }) => key);
            const value = selectedRows.length === keys.length && keys.length !== 0;

            return (
                <Checkbox
                    name="selected-state"
                    value={value}
                    onChange={() => selectAllRows(value ? [] : keys)}
                />
            );
        },
        accessor: "key",
        filterable: false,
        sortable: false,
        Cell: (row) => {
            const { key } = row.original;
            const keys = selectedRows.map((tableRow) => tableRow.key);

            return (
                <Checkbox
                    classes="-no-margin"
                    name={`select_${row.original.key}`}
                    value={keys.includes(key)}
                    onChange={(e) => updateRowSelection(row.original, e.currentTarget.value)}
                />
            );
        },
        width: 40,
        className: "txt-center",
    },
    {
        Header: WithTooltip("UPC"),
        accessor: "upc",
        Cell: (row) => {
            let upc = "";
            const isUpcValid = row.original.isUpcValid;

            if (row.original.shipmentProduct.modifier) {
                upc = `${row.value}-${row.original.shipmentProduct.modifier}`;
            } else {
                upc = `${row.value}`;
            }

            return (
                <div
                    style={{ color: isUpcValid ? "#444444" : "#8e2323" }}
                    title={!isUpcValid ? "Improper UPC" : upc}
                >
                    {upc}
                </div>
            );
        },
        Aggregated: () => null,
    },
    {
        Header: WithTooltip("CES Client"),
        accessor: "cxHClient",
        Filter: inputFilter,
        filterable,
        Aggregated: () => null,
    },
    {
        Header: WithTooltip("GDN Client"),
        accessor: "gdnClient",
        Filter: inputFilter,
        filterable,
        Aggregated: () => null,
    },
    // {
    //     Header: WithTooltip("Brand"),
    //     accessor: "brand",
    //     Filter: inputFilter,
    //     filterable: true,
    //     Aggregated: () => null,
    // },
    // {
    //     Header: WithTooltip("Manufacturer"),
    //     accessor: "manufacturer",
    //     Filter: inputFilter,
    //     filterable: true,
    //     Aggregated: () => null,
    // },
    {
        Header: WithTooltip("Bin ID"),
        accessor: "binTrackingNumber",
        Filter: inputFilter,
        filterable,
        Aggregated: () => null,
    },
    {
        Header: WithTooltip("Tracking #"),
        accessor: "shipment.trackingNumbers",
        Cell: (rowInfo) => {
            return rowInfo.value ? rowInfo.value.join(", ") : "";
        },
        Filter: inputFilter,
        filterable,
        sortable: false,
        Aggregated: () => null,
    },
    {
        Header: WithTooltip("Supplier"),
        accessor: "supplier",
        Filter: inputFilter,
        filterable,
        Aggregated: () => null,
    },
    {
        Header: WithTooltip("ISID"),
        accessor: "internalSupplierId",
        Filter: inputFilter,
        filterable,
        Aggregated: () => null,
    },
    {
        Header: WithTooltip("Shipment ID"),
        accessor: "shipment.key",
        Cell: (rowInfo) => {
            return (
                <a
                    href={`/shipment-editor/${rowInfo.value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {rowInfo.value}
                </a>
            );
        },
        Filter: inputFilter,
        filterable,
        Aggregated: () => null,
    },
    {
        Header: WithTooltip("Correlation ID"),
        accessor: "correlationId",
        Filter: inputFilter,
        filterable,
        sortable: true,
        Aggregated: () => null,
    },
    {
        Header: WithTooltip("Received Date"),
        accessor: "createDate",
        filterable,
        Filter: ({ filter, onChange }) => {
            return (
                <DueDateFilterButton
                    filterProp={filter}
                    onChangeProp={onChange}
                />
            );
        },
        Cell: (row) => {
            const dateString = row.value ? format(new Date(row.value), DATE_FNS_LONG_DATE_FORMAT) : "";

            return WithTooltip(dateString);
        },
    },
    {
        Header: WithTooltip("Updated"),
        accessor: "updatedOn",
        filterable,
        Filter: ({ filter, onChange }) => {
            return (
                <DueDateFilterButton
                    filterProp={filter}
                    onChangeProp={onChange}
                />
            );
        },
        Cell: (row) => {
            const dateString = row.value ? format(new Date(row.value), DATE_FNS_LONG_DATE_FORMAT) : "";

            return WithTooltip(dateString);
        },
    },
    {
        Header: WithTooltip("Due Date"),
        accessor: "shipment.dueDate",
        filterable,
        Filter: ({ filter, onChange }) => {
            return (
                <DueDateFilterButton
                    filterProp={filter}
                    onChangeProp={onChange}
                />
            );
        },
        Cell: (row) => {
            const dateString = row.value ? format(new Date(row.value), DATE_FNS_LONG_DATE_FORMAT) : "";

            return WithTooltip(dateString);
        },
    },
    {
        Header: WithTooltip("Status"),
        accessor: "status",
        Cell: (row) => {
            const status = CONTEXT_STATUS[row.original.status];

            return status ? status.text : row.original.status;
        },
        Filter: ({ filter, onChange }) => {
            return (
                <Select
                    onChange={(event) => onChange(event.target.value)}
                    style={{ width: "100%" }}
                    value={(filter && filter.value) || ""}
                    excludeSelect={false}
                    id="select-all-filter"
                    options={statusBooleanOptions}
                />
            );
        },
        filterable,
    },
    {
        Header: WithIcon("Shipment Status", faInfoCircle),
        accessor: "shipment.key",
        Cell: (row) => {
            return (
                <a
                    href={`/shipment-status/${row.value}#${row.original.key}`}
                    title="Shipment Status"
                    className="button -text"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <FontAwesomeIcon icon={faInfoCircle} />
                </a>
            );
        },
        width: 50,
        filterable: false,
        Aggregated: () => null,
    },
    {
        Header: WithIcon("Shipment Notes", faStickyNote),
        accessor: "shipment.key",
        Cell: ({ original }) => {
            return original.hasNotes ? (
                <div
                    className="button -text"
                    onClick={() =>
                        showNotes({
                            shipmentKey: original.shipmentKey,
                            shipmentProductKey: original.shipmentProductKey,
                            binTrackingNumber: original.binTrackingNumber,
                            clientCode: original.cxHClientCode,
                            upc: original.shipmentProduct.modifier ? `${original.upc}-${original.shipmentProduct.modifier}` : original.upc,
                        })
                    }
                >
                    <FontAwesomeIcon icon={faStickyNote} />
                </div>
            ) : null;
        },
        Aggregated: () => {
            return null;
        },
        width: 50,
        filterable: false,
    },
];
